import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { AppLogFilters, LogCategory, LogLevel } from '../DiagnosticDashboard';
import { fetchOrganisations } from '../../services/organisations.service';

interface OrgOption {
  _id: string;
  name?: string;
}

interface LogFiltersProps {
  filters: AppLogFilters;
  onChange: (filters: AppLogFilters) => void;
}

const CATEGORIES: LogCategory[] = [
  'http_error',
  'exception',
  'slow_request',
  'third_party',
  'db_error',
  'app_error',
  'security',
];

const LEVELS: LogLevel[] = ['error', 'warn', 'info'];

export default function LogFilters({ filters, onChange }: LogFiltersProps) {
  const [draft, setDraft] = useState<AppLogFilters>(filters);
  const [orgList, setOrgList] = useState<OrgOption[]>([]);

  useEffect(() => {
    fetchOrganisations()
      .then((data: any) => setOrgList(data?.organisations ?? []))
      .catch(() => {});
  }, []);

  const handleApply = () => onChange(draft);

  const handleClear = () => {
    const empty: AppLogFilters = {};
    setDraft(empty);
    onChange(empty);
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        flexWrap="wrap"
        alignItems="flex-end"
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={draft.category ?? ''}
            label="Category"
            onChange={(e) =>
              setDraft({ ...draft, category: (e.target.value as LogCategory) || undefined })
            }
          >
            <MenuItem value="">All</MenuItem>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Level</InputLabel>
          <Select
            value={draft.level ?? ''}
            label="Level"
            onChange={(e) =>
              setDraft({ ...draft, level: (e.target.value as LogLevel) || undefined })
            }
          >
            <MenuItem value="">All</MenuItem>
            {LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          select
          size="small"
          label="Organisation"
          value={draft.orgId ?? ''}
          onChange={(e) => setDraft({ ...draft, orgId: e.target.value || undefined })}
          sx={{ minWidth: 200 }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                style: { maxHeight: 260, overflowY: 'auto' },
              },
            },
          }}
        >
          <MenuItem value="">All organisations</MenuItem>
          {orgList.map((o) => (
            <MenuItem key={o._id} value={o._id}>
              {o.name?.trim() || o._id}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label="From"
          type="date"
          value={draft.from ? draft.from.slice(0, 10) : ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              from: e.target.value ? new Date(e.target.value).toISOString() : undefined,
            })
          }
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />

        <TextField
          size="small"
          label="To"
          type="date"
          value={draft.to ? draft.to.slice(0, 10) : ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              to: e.target.value
                ? new Date(e.target.value + 'T23:59:59.999Z').toISOString()
                : undefined,
            })
          }
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />

        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={handleApply}>
            Apply
          </Button>
          <Button variant="outlined" size="small" onClick={handleClear}>
            Clear
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
